import { useState, useMemo } from "react";
import BotonMapaProceso from "./BotonMapaProceso";
import { subirDatosSingle } from "./syncSupabase";
import { authHeader } from './supabase';
import { calcularNivelCA } from './nivelCalidadAsegurada';

const Q = {
  azul:"#1C3FAA", azulVivo:"#2A7FFF", azulMedio:"#6B6FD6",
  grisClaro:"#E8E9F6", grisPalido:"#F4F6FB", grisOscuro:"#2B2F36",
  grisPlata:"#8E93A1", blanco:"#FFFFFF",
};

const storage = { get: (k) => { try { return JSON.parse(localStorage.getItem(k)||'[]'); } catch { return []; } } };

function leerDatos() {
  return {
    ncs:          storage.get('alqos_ncs'),
    acciones:     storage.get('alqos_acciones_correctivas'),
    fmeas:        storage.get('alqos_fmea'),
    planControl:  storage.get('alqos_plancontrol'),
    tpm:          storage.get('alqos_tpm'),
    auditorias:   storage.get('alqos_auditorias'),
    docs:         storage.get('alqos_docs'),
    analisisCausa:storage.get('alqos_analisis_causa'),
    programa:     storage.get('alqos_programa_auditorias'),
    fsc:          storage.get('alqos_fsc_arqueo'),
  };
}

function calcularKPIs(datos) {
  const hoy = new Date(); hoy.setHours(0,0,0,0);
  const mesActual = new Date();
  const ncAbiertas = datos.ncs.filter(n=>n.estado!=="cerrada"&&n.estado!=="Cerrada").length;
  const accionesVencidas = datos.acciones.filter(a=>a.estado!=="cerrada"&&a.fechaCompromiso&&new Date(a.fechaCompromiso)<hoy).length;
  const fmeaCriticos = datos.fmeas.filter(f=>f.critico||f.npr>=100).length;
  const tienePCActivo = datos.planControl.some(p=>p.idFMEARelacionado);
  const tieneAccionesVinculadas = datos.acciones.some(a=>a.idNC);
  const tpmVencidos = datos.tpm.filter(t=>t.fechaProgramada&&new Date(t.fechaProgramada)<hoy&&t.estado!=="completado").length;
  const docsVencidos = datos.docs.filter(d=>{ if(!d.fechaVencimiento||d.estado==="obsoleto") return false; return new Date(d.fechaVencimiento)<hoy; }).length;
  const auditoriasEjecutadas = datos.programa.filter(p=>p.estado==="ejecutada").length;
  const accionesEficaces = datos.acciones.filter(a=>a.resultadoEficacia==="eficaz").length;
  const totalAcciones = datos.acciones.length;
  const pctEficacia = totalAcciones>0 ? Math.round((accionesEficaces/totalAcciones)*100) : 0;
  const desviosMes = datos.ncs.filter(n=>{ try{const f=new Date(n.fechaHora||n.fechaCreacion||""); return f.getMonth()===mesActual.getMonth()&&f.getFullYear()===mesActual.getFullYear();} catch{return false;} }).length;
  const hallazgosMayores = datos.auditorias.reduce((acc,a)=>acc+(a.hallazgos?.filter(h=>h.tipo==="nc_mayor").length||0),0);
  const analisisConCausaRaiz = datos.analisisCausa.filter(a=>a.causaRaizFinal).length;

  return {
    ncAbiertas, accionesVencidas, fmeaCriticos, tienePCActivo,
    tieneAccionesVinculadas, tpmVencidos, docsVencidos, auditoriasEjecutadas,
    accionesEficaces, totalAcciones, pctEficacia, desviosMes, hallazgosMayores,
    analisisConCausaRaiz,
    totalNC: datos.ncs.length,
    totalRegistros: datos.fmeas.length + datos.ncs.length + datos.planControl.length + datos.acciones.length,
    totalDocs: datos.docs.length,
    docsVigentes: datos.docs.filter(d=>d.estado==="vigente").length,
    totalAuditorias: datos.auditorias.length,
    auditoriasCompletadas: datos.auditorias.filter(a=>a.estado==="completada").length,
    totalAnalisis: datos.analisisCausa.length,
  };
}

function calcularBrecha(kpis, nivelActual, umbrales) {
  if (nivelActual === "Oro") return [];
  if (nivelActual === "Plata" || nivelActual === "Sin datos") {
    const brechas = [];
    if (kpis.ncAbiertas >= umbrales.oroNC) brechas.push({ texto: `Reducir NC abiertas a menos de ${umbrales.oroNC} (ahora: ${kpis.ncAbiertas})`, icono: "🔴" });
    if (kpis.accionesVencidas > 0) brechas.push({ texto: `Cerrar ${kpis.accionesVencidas} acción/es vencida/s`, icono: "🔴" });
    if (kpis.fmeaCriticos > 0) brechas.push({ texto: `Resolver ${kpis.fmeaCriticos} FMEA crítico/s (NPR ≥ 100)`, icono: "🔴" });
    if (!kpis.tienePCActivo) brechas.push({ texto: "Vincular Plan de Control a un FMEA", icono: "🟡" });
    if (kpis.tpmVencidos > 0) brechas.push({ texto: `Ejecutar ${kpis.tpmVencidos} tarea/s TPM vencida/s`, icono: "🟡" });
    if (kpis.docsVencidos > 0) brechas.push({ texto: `Renovar ${kpis.docsVencidos} documento/s vencido/s`, icono: "🟡" });
    if (kpis.auditoriasEjecutadas === 0) brechas.push({ texto: "Ejecutar al menos una auditoría interna", icono: "🟡" });
    return brechas;
  }
  // Bronce → Plata
  const brechas = [];
  if (kpis.ncAbiertas >= umbrales.plataNC) brechas.push({ texto: `Reducir NC abiertas a menos de ${umbrales.plataNC} (ahora: ${kpis.ncAbiertas})`, icono: "🔴" });
  if (kpis.accionesVencidas > 0) brechas.push({ texto: `Cerrar ${kpis.accionesVencidas} acción/es vencida/s`, icono: "🔴" });
  if (!kpis.tieneAccionesVinculadas) brechas.push({ texto: "Vincular acciones correctivas a NC existentes", icono: "🟡" });
  return brechas;
}

function exportarInsignia(nivel, cliente, periodo) {
  const colores = { Oro: ["#f59e0b","#fef3c7","#92400e"], Plata: ["#6b7280","#f3f4f6","#374151"], Bronce: ["#cd7f32","#fdf8f0","#78350f"] };
  const [col, bg, texto] = colores[nivel.nivel] || ["#808085","#f0f0f0","#333"];
  const iconos = { Oro: "🥇", Plata: "🥈", Bronce: "🥉" };
  const fecha = new Date().toLocaleDateString('es-AR');
  const html = `<!DOCTYPE html>
<html lang="es">
<head><meta charset="utf-8"><title>Insignia ${nivel.nivel} — ALQOS™</title>
<style>
* { margin:0; padding:0; box-sizing:border-box; }
body { background:#f0f4ff; display:flex; align-items:center; justify-content:center; min-height:100vh; font-family:Arial,sans-serif; }
.card { background:white; border-radius:24px; padding:48px 56px; text-align:center; box-shadow:0 8px 40px rgba(0,0,0,0.15); max-width:480px; width:90%; border-top:8px solid ${col}; }
.logo { font-size:13px; font-weight:700; color:#1C3FAA; letter-spacing:.15em; margin-bottom:32px; }
.icono { font-size:96px; margin-bottom:16px; }
.nivel { font-size:48px; font-weight:800; color:${col}; margin-bottom:8px; }
.programa { font-size:14px; color:#555; font-weight:600; margin-bottom:32px; }
.cliente { background:${bg}; border:2px solid ${col}; border-radius:12px; padding:16px 24px; margin-bottom:24px; }
.cliente-label { font-size:11px; color:#888; text-transform:uppercase; letter-spacing:.1em; margin-bottom:4px; }
.cliente-nombre { font-size:20px; font-weight:700; color:${texto}; }
.periodo { font-size:12px; color:#888; margin-bottom:32px; }
.firma { border-top:1px solid #eee; padding-top:24px; font-size:11px; color:#aaa; }
.firma strong { color:#1C3FAA; }
</style>
</head>
<body>
<div class="card">
  <div class="logo">ALQOS™ · ASSURED QUALITY PROGRAM</div>
  <div class="icono">${iconos[nivel.nivel]}</div>
  <div class="nivel">Nivel ${nivel.nivel}</div>
  <div class="programa">Programa de Calidad Asegurada by QORVEX</div>
  ${cliente ? `<div class="cliente"><div class="cliente-label">Otorgado a</div><div class="cliente-nombre">${cliente}</div></div>` : ""}
  ${periodo ? `<div class="periodo">Período: ${periodo}</div>` : ""}
  <div class="firma">Emitido el ${fecha} por <strong>ALQOS™ · QORVEX</strong></div>
</div>
</body></html>`;
  const w = window.open("","_blank");
  w.document.write(html);
  w.document.close();
  setTimeout(() => w.print(), 600);
}

function exportarReportePDF(kpis, nivel, cliente, periodo, analisisTexto) {
  const fecha = new Date().toLocaleDateString('es-AR');
  const colNivel = nivel.color;

  const html = `<!DOCTYPE html>
<html lang="es">
<head>
<meta charset="utf-8">
<title>Reporte Assured Quality — ALQOS™</title>
<style>
  * { margin:0; padding:0; box-sizing:border-box; }
  body { font-family: Arial, sans-serif; font-size: 11px; color: #2B2F36; background: white; }
  .header { background: #1C3FAA; color: white; padding: 32px 40px; }
  .header h1 { font-size: 24px; font-weight: 800; margin-bottom: 4px; }
  .header p { font-size: 12px; opacity: 0.8; }
  .badge-section { padding: 32px 40px; text-align: center; background: ${nivel.colorBg}; border-bottom: 3px solid ${colNivel}; }
  .badge-icon { font-size: 64px; margin-bottom: 8px; }
  .badge-nivel { font-size: 36px; font-weight: 800; color: ${colNivel}; margin-bottom: 8px; }
  .badge-desc { font-size: 13px; color: #555; }
  .section { padding: 24px 40px; border-bottom: 1px solid #eee; }
  .section h2 { font-size: 13px; font-weight: 700; color: #1C3FAA; text-transform: uppercase; letter-spacing: .1em; margin-bottom: 16px; }
  .kpi-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
  .kpi-card { background: #F4F6FB; border-radius: 8px; padding: 12px; text-align: center; }
  .kpi-val { font-size: 22px; font-weight: 800; margin-bottom: 4px; }
  .kpi-label { font-size: 10px; color: #555; }
  .criterio { display: flex; justify-content: space-between; padding: 7px 0; border-bottom: 0.5px solid #eee; font-size: 11px; }
  .ok { color: #22c55e; font-weight: 700; }
  .no { color: #e63946; font-weight: 700; }
  .clausula { display: flex; justify-content: space-between; padding: 6px 0; border-bottom: 0.5px solid #eee; font-size: 11px; }
  .clausula-code { font-weight: 700; color: #1C3FAA; background: #dbeafe; padding: 2px 6px; border-radius: 4px; font-size: 10px; }
  .analisis { background: #f8faff; border-left: 4px solid #2A7FFF; padding: 16px; border-radius: 4px; font-size: 11px; line-height: 1.7; white-space: pre-wrap; }
  .footer { padding: 20px 40px; background: #1C3FAA; color: white; display: flex; justify-content: space-between; font-size: 10px; }
  .cliente-box { background: white; border: 2px solid ${colNivel}; border-radius: 8px; padding: 16px; margin-bottom: 16px; }
  .cliente-box h3 { font-size: 12px; color: #555; margin-bottom: 4px; }
  .cliente-box p { font-size: 16px; font-weight: 700; color: #1C3FAA; }
</style>
</head>
<body>
<div class="header">
  <h1>ALQOS™ — Assured Quality Report</h1>
  <p>Sistema de Gestión Integral · Powered by QORVEX · Fecha: ${fecha}</p>
</div>

<div class="badge-section">
  <div class="badge-icon">${nivel.icono}</div>
  <div class="badge-nivel">Nivel ${nivel.nivel}</div>
  <div class="badge-desc">Período evaluado: ${periodo || "Acumulado a la fecha"}</div>
</div>

<div class="section">
  <h2>Destinatario</h2>
  <div class="cliente-box">
    <h3>Cliente / Organización auditora</h3>
    <p>${cliente || "— completar nombre del cliente —"}</p>
  </div>
  <div class="cliente-box">
    <h3>Empresa evaluada</h3>
    <p>Pyme industrial — Sistema ALQOS™ certificado</p>
  </div>
</div>

<div class="section">
  <h2>KPIs del sistema de gestión</h2>
  <div class="kpi-grid">
    <div class="kpi-card"><div class="kpi-val" style="color:${kpis.ncAbiertas<5?"#22c55e":"#e63946"}">${kpis.ncAbiertas}</div><div class="kpi-label">NC Abiertas</div></div>
    <div class="kpi-card"><div class="kpi-val" style="color:${kpis.accionesVencidas===0?"#22c55e":"#e63946"}">${kpis.accionesVencidas}</div><div class="kpi-label">Acc. Vencidas</div></div>
    <div class="kpi-card"><div class="kpi-val" style="color:${kpis.fmeaCriticos===0?"#22c55e":"#e63946"}">${kpis.fmeaCriticos}</div><div class="kpi-label">FMEA Críticos</div></div>
    <div class="kpi-card"><div class="kpi-val" style="color:#22c55e">${kpis.pctEficacia}%</div><div class="kpi-label">% Eficacia AC</div></div>
    <div class="kpi-card"><div class="kpi-val" style="color:${kpis.docsVencidos===0?"#22c55e":"#e63946"}">${kpis.docsVencidos}</div><div class="kpi-label">Docs Vencidos</div></div>
    <div class="kpi-card"><div class="kpi-val" style="color:#22c55e">${kpis.auditoriasCompletadas}</div><div class="kpi-label">Auditorías completadas</div></div>
    <div class="kpi-card"><div class="kpi-val" style="color:#22c55e">${kpis.totalDocs}</div><div class="kpi-label">Documentos controlados</div></div>
    <div class="kpi-card"><div class="kpi-val" style="color:#22c55e">${kpis.analisisConCausaRaiz}</div><div class="kpi-label">Análisis causa raíz</div></div>
    <div class="kpi-card"><div class="kpi-val" style="color:${kpis.tpmVencidos===0?"#22c55e":"#e63946"}">${kpis.tpmVencidos}</div><div class="kpi-label">TPM Vencidos</div></div>
  </div>
</div>

<div class="section">
  <h2>Criterios del nivel ${nivel.nivel}</h2>
  ${nivel.nivel==="Oro"?`
  <div class="criterio"><span>NC abiertas < 5</span><span class="${kpis.ncAbiertas<5?"ok":"no"}">${kpis.ncAbiertas<5?"✅ Cumple":"❌ No cumple"} (${kpis.ncAbiertas})</span></div>
  <div class="criterio"><span>Sin acciones vencidas</span><span class="${kpis.accionesVencidas===0?"ok":"no"}">${kpis.accionesVencidas===0?"✅ Cumple":"❌ No cumple"}</span></div>
  <div class="criterio"><span>Sin FMEA críticos</span><span class="${kpis.fmeaCriticos===0?"ok":"no"}">${kpis.fmeaCriticos===0?"✅ Cumple":"❌ No cumple"}</span></div>
  <div class="criterio"><span>Plan de Control vinculado</span><span class="${kpis.tienePCActivo?"ok":"no"}">${kpis.tienePCActivo?"✅ Cumple":"❌ No cumple"}</span></div>
  <div class="criterio"><span>Sin TPM vencidos</span><span class="${kpis.tpmVencidos===0?"ok":"no"}">${kpis.tpmVencidos===0?"✅ Cumple":"❌ No cumple"}</span></div>
  <div class="criterio"><span>Sin documentos vencidos</span><span class="${kpis.docsVencidos===0?"ok":"no"}">${kpis.docsVencidos===0?"✅ Cumple":"❌ No cumple"}</span></div>
  <div class="criterio"><span>Auditorías ejecutadas</span><span class="${kpis.auditoriasEjecutadas>0?"ok":"no"}">${kpis.auditoriasEjecutadas>0?"✅ Cumple":"❌ No cumple"}</span></div>
  `:nivel.nivel==="Plata"?`
  <div class="criterio"><span>NC abiertas < 10</span><span class="${kpis.ncAbiertas<10?"ok":"no"}">${kpis.ncAbiertas<10?"✅ Cumple":"❌ No cumple"} (${kpis.ncAbiertas})</span></div>
  <div class="criterio"><span>Sin acciones vencidas</span><span class="${kpis.accionesVencidas===0?"ok":"no"}">${kpis.accionesVencidas===0?"✅ Cumple":"❌ No cumple"}</span></div>
  <div class="criterio"><span>Acciones vinculadas a NC</span><span class="${kpis.tieneAccionesVinculadas?"ok":"no"}">${kpis.tieneAccionesVinculadas?"✅ Cumple":"❌ No cumple"}</span></div>
  `:`
  <div class="criterio"><span>Sistema implementado con datos</span><span class="ok">✅ Cumple</span></div>
  `}
</div>

<div class="section">
  <h2>Cláusulas ISO 9001:2015 cubiertas</h2>
  <div class="clausula"><span><span class="clausula-code">§ 6.1</span> Acciones para abordar riesgos</span><span>${kpis.totalAnalisis>0?"✅":"⬜"}</span></div>
  <div class="clausula"><span><span class="clausula-code">§ 7.5</span> Control de información documentada</span><span>${kpis.totalDocs>0?"✅":"⬜"}</span></div>
  <div class="clausula"><span><span class="clausula-code">§ 8.7</span> Control de productos no conformes</span><span>${kpis.totalNC>0?"✅":"⬜"}</span></div>
  <div class="clausula"><span><span class="clausula-code">§ 9.1</span> Seguimiento, medición y análisis</span><span>${kpis.totalNC>0?"✅":"⬜"}</span></div>
  <div class="clausula"><span><span class="clausula-code">§ 9.2</span> Auditoría interna</span><span>${kpis.totalAuditorias>0?"✅":"⬜"}</span></div>
  <div class="clausula"><span><span class="clausula-code">§ 10.2</span> No conformidad y acción correctiva</span><span>${kpis.totalAcciones>0?"✅":"⬜"}</span></div>
  <div class="clausula"><span><span class="clausula-code">§ 10.3</span> Mejora continua</span><span>${kpis.analisisConCausaRaiz>0?"✅":"⬜"}</span></div>
</div>

${analisisTexto?`
<div class="section">
  <h2>Análisis ejecutivo ARIA</h2>
  <div class="analisis">${analisisTexto}</div>
</div>`:""}

<div class="footer">
  <span>ALQOS™ by QORVEX · Sistema de Gestión Integral</span>
  <span>Generado: ${fecha} · Nivel ${nivel.nivel} ${nivel.icono}</span>
</div>
</body>
</html>`;

  const w = window.open("","_blank");
  w.document.write(html);
  w.document.close();
  setTimeout(()=>w.print(), 600);
}

export default function AssuredQuality({ onVolver, oscuro, usuario }) {
  const [datos, setDatos] = useState(() => leerDatos());
  const [cliente, setCliente] = useState("");
  const [periodo, setPeriodo] = useState("");
  const [cargandoARIA, setCargandoARIA] = useState(false);
  const [analisisTexto, setAnalisisTexto] = useState("");
  const [historial, setHistorial] = useState(() => {
    try { return JSON.parse(localStorage.getItem('alqos_aq_historial')||'[]'); } catch { return []; }
  });
  const [umbrales, setUmbrales] = useState(() => {
    try { return JSON.parse(localStorage.getItem('alqos_aq_umbrales')||'null') || { oroNC:5, plataNC:10 }; } catch { return { oroNC:5, plataNC:10 }; }
  });
  const [editandoUmbrales, setEditandoUmbrales] = useState(false);

  const bg     = oscuro ? "#09184D" : Q.grisPalido;
  const cardBg = oscuro ? "#0d2160" : Q.blanco;
  const texto  = oscuro ? "#EDF2FA" : Q.grisOscuro;
  const borde  = oscuro ? "#1e3a8a" : Q.grisClaro;
  const navBg  = oscuro ? "#060f30" : Q.azul;
  const sub    = oscuro ? "#9aabd4" : Q.grisPlata;
  const card   = { background:cardBg, borderRadius:"14px", padding:"1.5rem", border:`1px solid ${borde}`, marginBottom:"14px" };
  const inp    = { width:"100%", padding:"8px 12px", borderRadius:"8px", border:`1px solid ${borde}`, fontSize:"13px", boxSizing:"border-box", marginTop:"4px", background:cardBg, color:texto, fontFamily:"sans-serif" };
  const lbl    = { fontSize:"11px", color:sub, fontWeight:"700", display:"block", marginTop:"12px", textTransform:"uppercase", letterSpacing:".05em" };

  const kpis  = useMemo(() => calcularKPIs(datos), [datos]);
  const nivel = useMemo(() => calcularNivelCA(kpis, umbrales), [kpis, umbrales]);

  const refrescar = () => setDatos(leerDatos());

  const guardarEnHistorial = () => {
    const entrada = {
      fecha: new Date().toISOString(),
      nivel: nivel.nivel,
      cliente,
      periodo,
      ncAbiertas: kpis.ncAbiertas,
      pctEficacia: kpis.pctEficacia,
      docsVencidos: kpis.docsVencidos,
    };
    const nuevo = [entrada, ...historial].slice(0, 12);
    setHistorial(nuevo);
    localStorage.setItem('alqos_aq_historial', JSON.stringify(nuevo));
    subirDatosSingle('alqos_aq_historial', 'aq_historial');
  };

  const generarARIA = async () => {
    setCargandoARIA(true);
    try {
      const prompt = `Sos ARIA, asistente de ALQOS™. Generá un análisis ejecutivo breve para un reporte Assured Quality que se enviará a un cliente externo que auditará el proceso. Sé profesional, claro y objetivo. Sin markdown, sin asteriscos.

Nivel alcanzado: ${nivel.nivel} ${nivel.icono}
NC abiertas: ${kpis.ncAbiertas} | Acciones vencidas: ${kpis.accionesVencidas} | FMEA críticos: ${kpis.fmeaCriticos}
% Eficacia acciones correctivas: ${kpis.pctEficacia}%
Documentos vigentes: ${kpis.docsVigentes} | Vencidos: ${kpis.docsVencidos}
Auditorías completadas: ${kpis.auditoriasCompletadas} | Hallazgos mayores: ${kpis.hallazgosMayores}
Análisis de causa con causa raíz identificada: ${kpis.analisisConCausaRaiz}
TPM vencidos: ${kpis.tpmVencidos}

Estructura del análisis (máximo 300 palabras):
ESTADO GENERAL DEL SISTEMA
[2 párrafos sobre el estado actual]

FORTALEZAS IDENTIFICADAS
[2-3 fortalezas concretas basadas en los datos]

OPORTUNIDADES DE MEJORA
[2-3 oportunidades específicas]

CONCLUSIÓN PARA EL CLIENTE
[1 párrafo final profesional]`;

      const response = await fetch("/api/aria", {
        method:"POST",
        headers:{ ...(await authHeader()),
          "Content-Type":"application/json",
          "anthropic-version":"2023-06-01",
          },
        body: JSON.stringify({
          model:"claude-sonnet-4-5",
          max_tokens:800,
          messages:[{role:"user",content:prompt}]
        })
      });
      const data = await response.json();
      const raw = data.content?.[0]?.text||"";
      setAnalisisTexto(raw.replace(/```/g,"").trim());
    } catch(e) {
      alert("Error al conectar con ARIA.");
    }
    setCargandoARIA(false);
  };

  const handleExportar = () => {
    guardarEnHistorial();
    exportarReportePDF(kpis, nivel, cliente, periodo, analisisTexto);
  };

  const semaforo = (v,lv,la) => v<=lv?"#22c55e":v<=la?"#f59e0b":"#e63946";

  return (
    <div style={{ minHeight:"100vh", background:bg, fontFamily:"sans-serif" }}>
      <div style={{ background:navBg, padding:"1rem 1.5rem", display:"flex", alignItems:"center", justifyContent:"space-between", position:"sticky", top:0, zIndex:100 }}>
        <div style={{ display:"flex", alignItems:"center", gap:"12px" }}>
          <button onClick={onVolver} style={{ background:"rgba(255,255,255,0.1)", border:"none", color:"white", padding:"6px 14px", borderRadius:"10px", cursor:"pointer", fontSize:"13px" }}>← Menú</button>
          <span style={{ color:"white", fontWeight:"700", fontSize:"16px" }}>🏆 Assured Quality</span>
        </div>
        <div style={{ display:"flex", gap:"8px" }}>
          <button onClick={refrescar} style={{ padding:"6px 10px", background:"rgba(255,255,255,0.1)", border:"none", borderRadius:"8px", cursor:"pointer", fontSize:"12px", color:"white" }}>🔄</button>
          <button onClick={handleExportar} style={{ padding:"6px 14px", background:"linear-gradient(135deg,#f59e0b,#d97706)", border:"none", borderRadius:"8px", cursor:"pointer", fontSize:"13px", color:"white", fontWeight:"700" }}>📄 Exportar reporte</button>
          {usuario?.rol === "Administrador" && <BotonMapaProceso moduloKey="assured_quality" oscuro={oscuro} />}
        </div>
      </div>

      <div style={{ padding:"1rem 1.5rem", maxWidth:"840px", margin:"0 auto" }}>

        {/* BADGE NIVEL */}
        <div style={{ background:nivel.colorBg, borderRadius:"20px", padding:"2rem", border:`3px solid ${nivel.color}`, marginBottom:"1.5rem", textAlign:"center" }}>
          <p style={{ fontSize:"64px", margin:"0 0 8px" }}>{nivel.icono}</p>
          <h2 style={{ fontSize:"32px", fontWeight:"800", color:nivel.color, margin:"0 0 8px" }}>Nivel {nivel.nivel}</h2>
          <div style={{ display:"flex", justifyContent:"center", gap:"8px", flexWrap:"wrap", marginTop:"12px" }}>
            {[["Bronce","🥉","#cd7f32"],["Plata","🥈","#6b7280"],["Oro","🥇","#f59e0b"]].map(([n,ico,col])=>(
              <div key={n} style={{ padding:"6px 20px", borderRadius:"20px", fontSize:"13px", fontWeight:"700", background:nivel.nivel===n?`${col}22`:"transparent", border:`2px solid ${nivel.nivel===n?col:"#ddd"}`, color:nivel.nivel===n?col:oscuro?"#555":"#ccc" }}>{ico} {n}</div>
            ))}
          </div>
        </div>

        {/* DATOS DEL REPORTE */}
        <div style={card}>
          <h3 style={{ fontSize:"14px", fontWeight:"700", color:Q.azulVivo, marginBottom:"8px" }}>📋 Datos del reporte</h3>
          <span style={lbl}>Cliente / Organización auditora</span>
          <input value={cliente} onChange={e=>setCliente(e.target.value)} style={inp} placeholder="Ej: Empresa SA · Depto. Calidad"/>
          <span style={lbl}>Período evaluado</span>
          <input value={periodo} onChange={e=>setPeriodo(e.target.value)} style={inp} placeholder="Ej: Enero - Mayo 2026"/>
        </div>

        {/* KPIs RESUMEN */}
        <div style={card}>
          <h3 style={{ fontSize:"14px", fontWeight:"700", color:texto, marginBottom:"14px" }}>📊 KPIs del sistema</h3>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(140px,1fr))", gap:"10px" }}>
            {[
              {label:"NC Abiertas",      val:kpis.ncAbiertas,          color:semaforo(kpis.ncAbiertas,4,9)},
              {label:"Acc. Vencidas",    val:kpis.accionesVencidas,    color:semaforo(kpis.accionesVencidas,0,2)},
              {label:"FMEA Críticos",    val:kpis.fmeaCriticos,        color:semaforo(kpis.fmeaCriticos,0,2)},
              {label:"% Eficacia AC",    val:kpis.pctEficacia+"%",     color:"#22c55e"},
              {label:"Docs vencidos",    val:kpis.docsVencidos,        color:semaforo(kpis.docsVencidos,0,2)},
              {label:"Auditorías ✅",    val:kpis.auditoriasCompletadas,color:"#22c55e"},
              {label:"Docs vigentes",    val:kpis.docsVigentes,        color:"#22c55e"},
              {label:"Causa raíz ID",    val:kpis.analisisConCausaRaiz,color:"#22c55e"},
              {label:"TPM vencidos",     val:kpis.tpmVencidos,         color:semaforo(kpis.tpmVencidos,0,1)},
            ].map(({label,val,color})=>(
              <div key={label} style={{ background:oscuro?"#091840":Q.grisPalido, borderRadius:"10px", padding:"12px", textAlign:"center", border:`1px solid ${borde}` }}>
                <p style={{ fontSize:"22px", fontWeight:"800", color, margin:"0 0 4px" }}>{val}</p>
                <p style={{ fontSize:"10px", color:sub, margin:0 }}>{label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CRITERIOS */}
        <div style={{ ...card, background:oscuro?"#0a1f5c":"#eff6ff", border:`1px solid ${nivel.color}40` }}>
          <h3 style={{ fontSize:"14px", fontWeight:"700", color:nivel.color, marginBottom:"12px" }}>Criterios nivel {nivel.nivel} {nivel.icono}</h3>
          {nivel.nivel==="Oro"&&[
            {label:`NC abiertas < ${umbrales.oroNC}`,             ok:kpis.ncAbiertas<umbrales.oroNC,            actual:`${kpis.ncAbiertas} abiertas`},
            {label:"Sin acciones vencidas",        ok:kpis.accionesVencidas===0,    actual:`${kpis.accionesVencidas} vencidas`},
            {label:"Sin FMEA críticos",            ok:kpis.fmeaCriticos===0,        actual:`${kpis.fmeaCriticos} críticos`},
            {label:"Plan de Control activo",       ok:kpis.tienePCActivo,           actual:kpis.tienePCActivo?"Configurado":"Sin vincular"},
            {label:"Sin TPM vencidos",             ok:kpis.tpmVencidos===0,         actual:`${kpis.tpmVencidos} vencidos`},
            {label:"Sin documentos vencidos",      ok:kpis.docsVencidos===0,        actual:`${kpis.docsVencidos} vencidos`},
            {label:"Auditorías ejecutadas",        ok:kpis.auditoriasEjecutadas>0,  actual:kpis.auditoriasEjecutadas>0?"Ejecutadas":"Sin ejecutar"},
          ].map(({label,ok,actual})=>(
            <div key={label} style={{ display:"flex", justifyContent:"space-between", padding:"7px 0", borderBottom:`0.5px solid ${borde}`, fontSize:"13px" }}>
              <span style={{ display:"flex", alignItems:"center", gap:"8px" }}><span>{ok?"✅":"🔴"}</span>{label}</span>
              <span style={{ color:ok?"#22c55e":"#e63946", fontWeight:"600", fontSize:"11px" }}>{actual}</span>
            </div>
          ))}
          {nivel.nivel==="Plata"&&[
            {label:`NC abiertas < ${umbrales.plataNC}`,             ok:kpis.ncAbiertas<umbrales.plataNC,           actual:`${kpis.ncAbiertas} abiertas`},
            {label:"Sin acciones vencidas",        ok:kpis.accionesVencidas===0,    actual:`${kpis.accionesVencidas} vencidas`},
            {label:"Acciones vinculadas a NC",     ok:kpis.tieneAccionesVinculadas, actual:kpis.tieneAccionesVinculadas?"Vinculadas":"Sin vincular"},
          ].map(({label,ok,actual})=>(
            <div key={label} style={{ display:"flex", justifyContent:"space-between", padding:"7px 0", borderBottom:`0.5px solid ${borde}`, fontSize:"13px" }}>
              <span style={{ display:"flex", alignItems:"center", gap:"8px" }}><span>{ok?"✅":"🔴"}</span>{label}</span>
              <span style={{ color:ok?"#22c55e":"#e63946", fontWeight:"600", fontSize:"11px" }}>{actual}</span>
            </div>
          ))}
          {nivel.nivel==="Bronce"&&(
            <p style={{ fontSize:"13px", color:sub }}>Sistema implementado. Trabajá en reducir NC abiertas y cerrar acciones vencidas para avanzar a Plata.</p>
          )}
        </div>

        {/* BRECHA HACIA SIGUIENTE NIVEL */}
        {nivel.nivel !== "Oro" && (
          <div style={{ ...card, background:oscuro?"#1a0a00":"#fffbeb", border:`1px solid #f59e0b40` }}>
            <h3 style={{ fontSize:"14px", fontWeight:"700", color:"#f59e0b", marginBottom:"12px" }}>
              🎯 ¿Qué falta para {nivel.nivel==="Bronce"?"Plata":"Oro"} {nivel.nivel==="Bronce"?"🥈":"🥇"}?
            </h3>
            {calcularBrecha(kpis, nivel.nivel, umbrales).length === 0
              ? <p style={{ fontSize:"13px", color:"#22c55e", fontWeight:"600" }}>✅ ¡Ya cumplís todos los criterios! Generá el reporte para confirmar el ascenso.</p>
              : calcularBrecha(kpis, nivel.nivel, umbrales).map((b,i) => (
                  <div key={i} style={{ display:"flex", alignItems:"center", gap:"10px", padding:"8px 0", borderBottom:`0.5px solid ${borde}`, fontSize:"13px" }}>
                    <span>{b.icono}</span>
                    <span style={{ color:texto }}>{b.texto}</span>
                  </div>
                ))
            }
          </div>
        )}

        {/* INSIGNIA DESCARGABLE */}
        <div style={{ ...card, textAlign:"center", background:oscuro?`${nivel.color}15`:nivel.colorBg, border:`2px solid ${nivel.color}40` }}>
          <p style={{ fontSize:"13px", fontWeight:"700", color:nivel.color, marginBottom:"8px" }}>🏅 Insignia descargable</p>
          <p style={{ fontSize:"12px", color:sub, marginBottom:"14px" }}>Certificado visual para compartir con el cliente</p>
          <button onClick={()=>exportarInsignia(nivel, cliente, periodo)}
            style={{ padding:"10px 24px", background:`linear-gradient(135deg,${nivel.color},${nivel.color}99)`, border:"none", borderRadius:"10px", cursor:"pointer", color:"white", fontWeight:"700", fontSize:"13px" }}>
            🖨️ Imprimir / Descargar insignia {nivel.icono}
          </button>
        </div>

        {/* UMBRALES PERSONALIZABLES */}
        <div style={card}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"12px" }}>
            <h3 style={{ fontSize:"14px", fontWeight:"700", color:texto, margin:0 }}>⚙️ Umbrales personalizables</h3>
            <button onClick={()=>setEditandoUmbrales(u=>!u)} style={{ padding:"5px 12px", background:"rgba(123,91,242,0.1)", border:"1px solid rgba(123,91,242,0.3)", borderRadius:"8px", cursor:"pointer", fontSize:"12px", color:"#7B5BF2" }}>
              {editandoUmbrales?"Guardar":"Editar"}
            </button>
          </div>
          {editandoUmbrales ? (
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"12px" }}>
              <div>
                <span style={lbl}>NC máx. para Oro</span>
                <input type="number" min="1" max="20" value={umbrales.oroNC} onChange={e=>{const n={...umbrales,oroNC:parseInt(e.target.value)};setUmbrales(n);localStorage.setItem('alqos_aq_umbrales',JSON.stringify(n));subirDatosSingle('alqos_aq_umbrales','aq_umbrales');}} style={inp}/>
              </div>
              <div>
                <span style={lbl}>NC máx. para Plata</span>
                <input type="number" min="1" max="30" value={umbrales.plataNC} onChange={e=>{const n={...umbrales,plataNC:parseInt(e.target.value)};setUmbrales(n);localStorage.setItem('alqos_aq_umbrales',JSON.stringify(n));subirDatosSingle('alqos_aq_umbrales','aq_umbrales');}} style={inp}/>
              </div>
            </div>
          ) : (
            <div style={{ display:"flex", gap:"16px", fontSize:"13px" }}>
              <span style={{ color:sub }}>Oro: NC &lt; <strong style={{ color:"#f59e0b" }}>{umbrales.oroNC}</strong></span>
              <span style={{ color:sub }}>Plata: NC &lt; <strong style={{ color:"#6b7280" }}>{umbrales.plataNC}</strong></span>
            </div>
          )}
        </div>

        {/* ANÁLISIS ARIA */}
        <div style={{ ...card, background:oscuro?"#050e2e":"#f8faff", border:`1px solid ${Q.azulVivo}30` }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"12px" }}>
            <h3 style={{ fontSize:"14px", fontWeight:"700", color:Q.azul, margin:0 }}>🤖 Análisis ejecutivo ARIA</h3>
            <button onClick={generarARIA} disabled={cargandoARIA}
              style={{ padding:"7px 14px", background:cargandoARIA?"rgba(42,127,255,.3)":`linear-gradient(135deg,${Q.azulVivo},${Q.azul})`, border:"none", borderRadius:"8px", cursor:cargandoARIA?"default":"pointer", color:"white", fontSize:"12px", fontWeight:"700" }}>
              {cargandoARIA?"🤖 Generando...":"🤖 Generar análisis"}
            </button>
          </div>
          <p style={{ fontSize:"12px", color:sub, margin:"0 0 12px" }}>ARIA genera un análisis profesional para incluir en el reporte al cliente</p>
          {analisisTexto ? (
            <div style={{ background:oscuro?"#0a1f5c":"white", borderRadius:"10px", padding:"14px", border:`1px solid ${borde}`, fontSize:"13px", color:texto, lineHeight:"1.7", whiteSpace:"pre-wrap" }}>
              {analisisTexto}
            </div>
          ) : (
            <div style={{ background:oscuro?"#091840":Q.grisPalido, borderRadius:"10px", padding:"20px", textAlign:"center", border:`1px solid ${borde}` }}>
              <p style={{ fontSize:"13px", color:sub }}>Hacé clic en "Generar análisis" para que ARIA redacte el informe ejecutivo</p>
            </div>
          )}
        </div>

        {/* HISTORIAL */}
        {historial.length > 0 && (
          <div style={card}>
            <h3 style={{ fontSize:"14px", fontWeight:"700", color:texto, marginBottom:"12px" }}>📅 Historial de reportes</h3>
            {historial.map((h,i)=>{
              const anterior = historial[i+1];
              const niveles = { Bronce:1, Plata:2, Oro:3 };
              const tendencia = anterior ? (niveles[h.nivel]>niveles[anterior.nivel]?"📈":niveles[h.nivel]<niveles[anterior.nivel]?"📉":"➡️") : "";
              return (
                <div key={i} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"8px 0", borderBottom:`0.5px solid ${borde}`, fontSize:"12px" }}>
                  <span style={{ color:sub }}>{new Date(h.fecha).toLocaleDateString('es-AR')}</span>
                  <span style={{ fontWeight:"700", color:h.nivel==="Oro"?"#f59e0b":h.nivel==="Plata"?"#6b7280":"#cd7f32" }}>{h.nivel==="Oro"?"🥇":h.nivel==="Plata"?"🥈":"🥉"} {h.nivel} {tendencia}</span>
                  <span style={{ color:sub }}>{h.cliente||"Sin cliente"}</span>
                  <span style={{ color:sub }}>{h.ncAbiertas} NC · {h.pctEficacia}% eficacia</span>
                </div>
              );
            })}
          </div>
        )}

      </div>
    </div>
  );
}
